import { Row, Col, Card, Table, Tag, Divider, Spin, Image, Badge } from "antd";
import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import moment from "moment";
import notfound from "../../assets/images/no-img.png";
import languages from "../../config/language";
import { ArrowLeftIcon } from "lucide-react";

function TempProductView() {
  const location = useLocation();
  const params = useParams();
  const { request } = useRequest();

  const [list, setList] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchData = (id) => {
    setLoading(true);
    request({
      url: apiPath.viewTempProduct + "/" + id,
      method: "GET",
      onSuccess: (data) => {
        setList(data.data);
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchData(params.id);
  }, [params.id]);

  const statusColorMap = {
    incomplete: "orange",
    pending_verification: "blue",
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={12}>
        <Card title="Product Details (QC Staging)">
          <Spin spinning={loading} tip="Loading...">
            <div className="view-main-list">

              <div className="view-inner-cls">
                <h5>Image:</h5>
                <h6>
                  <Image
                    className="imagefix"
                    width={80}
                    height={80}
                    src={list.image_url || notfound}
                    alt="Product"
                  />
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Part Number:</h5>
                <h6>{list.part_number || "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Ref. No.:</h5>
                <h6>{list.ref_no || "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Price (INR):</h5>
                <h6>{list.price ? `₹${list.price}` : "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Pack Size:</h5>
                <h6>{list.pack_size || "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Specification:</h5>
                <h6 style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {list.specification || "-"}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Slug:</h5>
                <h6>{list.slug || "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Status:</h5>
                <h6>
                  {list.is_active ? (
                    <Badge status="success" text="Active" />
                  ) : (
                    <Badge status="error" text="Inactive" />
                  )}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Created On:</h5>
                <h6>
                  {list.created_at
                    ? moment(list.created_at).format("DD-MMM-YYYY HH:mm")
                    : "-"}
                </h6>
              </div>

              <div className="view-inner-cls">
                <Link
                  className="ant-btn ant-btn-secondary"
                  to={`/product${location.search}`}
                >
                  <ArrowLeftIcon size={15} /> Back
                </Link>
              </div>
            </div>
          </Spin>
        </Card>

        <Card title="QC Status" className="mt-4">
          <Spin spinning={loading} tip="Loading...">
            <div className="view-inner-cls">
              <h5>QC Status:</h5>
              <h6>
                <Tag color={statusColorMap[list.status] || "default"}>
                  {list.status === "incomplete" ? "Incomplete" : list.status === "pending_verification" ? "Pending Verification" : list.status || "-"}
                </Tag>
              </h6>
            </div>

            <div className="view-inner-cls">
              <h5>Source:</h5>
              <h6>
                {list.source ? (
                  <Tag color={list.source === "excel_import" ? "blue" : "orange"}>
                    {list.source === "excel_import" ? "Excel Import (New)" : "Admin Edit"}
                  </Tag>
                ) : "-"}
              </h6>
            </div>

            <div className="view-inner-cls">
              <h5>Sent to QC At:</h5>
              <h6>
                {list.sent_to_qc_at
                  ? moment(list.sent_to_qc_at).format("DD-MMM-YYYY HH:mm")
                  : "-"}
              </h6>
            </div>

            <div className="view-inner-cls">
              <h5>Submitted By:</h5>
              <h6>{list.submitted_by?.name || "-"}</h6>
            </div>

            <div className="view-inner-cls">
              <h5>QC Reviewed By:</h5>
              <h6>{list.qc_reviewed_by?.name || "-"}</h6>
            </div>

            {list.qc_notes && (
              <div className="view-inner-cls">
                <h5>QC Notes:</h5>
                <h6 style={{ color: "#ff4d4f", whiteSpace: "pre-wrap" }}>{list.qc_notes}</h6>
              </div>
            )}

            {list.source_product_id && (
              <div className="view-inner-cls">
                <h5>Original Product:</h5>
                <h6>
                  <Link to={`/product/view/${list.source_product_id?._id || list.source_product_id}`}>
                    View Original
                  </Link>
                </h6>
              </div>
            )}
          </Spin>
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card title="Associations">
          <Spin spinning={loading} tip="Loading...">
            <div className="view-inner-cls">
              <h5>Segment:</h5>
              <h6>{list.segment_id?.en_name || "-"}</h6>
            </div>
            <div className="view-inner-cls">
              <h5>Brand:</h5>
              <h6>{list.brand_id?.en_name || "-"}</h6>
            </div>
            <div className="view-inner-cls">
              <h5>Category:</h5>
              <h6>{list.category_id?.en_name || "-"}</h6>
            </div>
            <div className="view-inner-cls">
              <h5>Sub Category:</h5>
              <h6>{list.sub_category_id?.en_name || "-"}</h6>
            </div>
          </Spin>
        </Card>

        <Card title="Vehicle Mapping Options" className="mt-4">
          <Spin spinning={loading} tip="Loading...">
            <div className="view-inner-cls mb-4">
              <h5>Mapping Option:</h5>
              <h6>
                {list.vehicle_mapping_option ? (
                  <Tag color={
                    list.vehicle_mapping_option === "all" ? "green" :
                      list.vehicle_mapping_option === "manufacturers" ? "red" :
                        list.vehicle_mapping_option === "specific" ? "purple" : "default"
                  } className="cap">
                    {list.vehicle_mapping_option}
                  </Tag>
                ) : "-"}
              </h6>
            </div>

            {list.vehicle_mapping_option === "manufacturers" && (
              <div className="mt-3">
                <h5>Selected Manufacturers:</h5>
                <div className="flex flex-wrap gap-2 mt-2">
                  {list.vehicle_manufacturer_ids?.map((m) => (
                    <Tag key={m._id} color="blue">{m.en_name}</Tag>
                  )) || "-"}
                </div>
              </div>
            )}

            {list.vehicle_mapping_option === "specific" && (
              <div className="mt-3">
                <h5>Vehicle Mappings:</h5>
                <Table
                  dataSource={list.vehicle_model_mappings || []}
                  pagination={false}
                  size="small"
                  rowKey="_id"
                  className="mt-2"
                  columns={[
                    {
                      title: "Manufacturer",
                      dataIndex: ["manufacturer_id", "en_name"],
                      key: "manufacturer",
                    },
                    {
                      title: "Vehicle",
                      dataIndex: ["vehicle_id", "en_name"],
                      key: "vehicle",
                    },
                    {
                      title: "Models",
                      key: "models",
                      render: (_, record) =>
                        record.model_selection === "all" ? (
                          <Tag color="cyan">All Models</Tag>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {record.specific_models?.map((m) => (
                              <Tag key={m._id} size="small">{m.en_name}</Tag>
                            )) || "-"}
                          </div>
                        ),
                    },
                  ]}
                />
              </div>
            )}
          </Spin>
        </Card>
      </Col>

      <Col xs={24}>
        <Card title="Language Data">
          <Spin spinning={loading} tip="Loading...">
            {languages.map((lang) => (
              <div key={lang.short} className="mb-3">
                <h4>{lang.long}</h4>
                <div className="view-inner-cls">
                  <h5>Name:</h5>
                  <h6>{list[`${lang.short}_name`] || "-"}</h6>
                </div>
                <div className="view-inner-cls">
                  <h5>Description:</h5>
                  <h6
                    className="lang-description"
                    dangerouslySetInnerHTML={{
                      __html: list[`${lang.short}_description`] || "-",
                    }}
                  />
                </div>
                <Divider />
              </div>
            ))}
          </Spin>
        </Card>
      </Col>
    </Row>
  );
}

export default TempProductView;
